import { SlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide';
import { Application } from '@tencent/alloy-renderer';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { PlayerLogicService } from '@src/slide/content-viewer/service/canvas-renderer/entry/player/logic/player-logic-service';
import { RenderPostionType } from '@src/js/constant';
import { IPlayerSlideRenderTree, UserTransform } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/player/type';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { v4 as uuidV4 } from 'uuid';
import { ISlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/types';

export class MobilePlayerSlideRenderTree implements IPlayerSlideRenderTree {
    public slideComponent: SlideComponent;
    public id: string;
    private application?: Application;
    private slideModel: ISlideModel;
    private onDidRenderCallback: (slideComponent: ISlideComponent) => void;
    private handleSlideClickRef?: any;
    private asyncBuild: boolean;
    private scale: number;
    private canvasWrapperDom: HTMLDivElement | null;
    private getPlayerReduxStore: () => any;
    private canvasDom: HTMLCanvasElement;

    public constructor(params: {
        slideModel: ISlideModel,
        asyncBuild: boolean,
        getPlayerReduxStore: () => any,
        canvasDom?: HTMLCanvasElement,
        application?: Application<any>,
        onDidRenderCallback: (slideComponent: ISlideComponent) => void,
    }) {
        const { slideModel, asyncBuild, getPlayerReduxStore, canvasDom, application, onDidRenderCallback } = params;
        this.slideModel = slideModel;
        this.asyncBuild = asyncBuild;
        this.getPlayerReduxStore = getPlayerReduxStore;
        this.onDidRenderCallback = onDidRenderCallback;
        this.scale = 1;
        this.id = uuidV4();
        if (canvasDom) {
            this.canvasDom = canvasDom;
        }
        if (application) {
            this.application = application;
        }
        this.build();
    }

    public dispose(): void {
        this.hidden();
        this.slideComponent?.dispose();
        this.application = undefined;
    }

    /**
     * 当前页面挂载之后，需要渲染
     * @param canvasWrapperDom
     * @param scale
     * @param handleSlideClick
     */
    public display(
        canvasWrapperDom: HTMLDivElement,
        scale = 1,
        handleSlideClick,
        userTransform: UserTransform,
    ): void {
        if (!this.application) return;

        let needRender = false;
        if (scale !== this.scale) {
            this.scale = scale;
            needRender = true;
        }
        this.handleSlideClickRef = handleSlideClick;
        if (this.application && canvasWrapperDom !== this.canvasWrapperDom) {
            canvasWrapperDom.appendChild(this.canvasDom);
            this.canvasWrapperDom = canvasWrapperDom;
            needRender = true;
        }
        // 如果还没有创建树，同步创建
        if (!this.slideComponent) {
            this.asyncBuild = false;
            this.build();
        }
        if (!this.slideComponent.hasBuild) {
            // 如果异步创建没有完成，直接同步创建
            this.slideComponent.display();
        }
        this.application.stage.addChild(this.slideComponent);
        if (needRender) {
            this.slideComponent.renderBackground();
            const width = this.getAppServices().getUiDataService().presentation.props.slideWidth * scale;
            const height = this.getAppServices().getUiDataService().presentation.props.slideHeight * scale;
            this.application.width = width;
            this.application.height = height;

            this.setTransform(userTransform);
        }
    }

    public hidden = (): void => {
        if (!this.application) {
            return;
        }
        this.application.stage.removeChild(this.slideComponent);
    };

    public getApplication = () => this.application as Application;

    public setTransform({ scaleX, scaleY, pivotX, pivotY }: UserTransform): void {
        if (!this.application) return;

        this.application.stage.transform.scaleX = scaleX * this.scale;
        this.application.stage.transform.scaleY = scaleY * this.scale;
        this.application.stage.transform.pivotX = pivotX;
        this.application.stage.transform.pivotY = pivotY;

        this.application.requestRender();
    }

    private getPageScale = () => this.scale;

    private handleSlideClick = (e, model) => {
        this.handleSlideClickRef?.(e, model);
    };

    /**
     * 根据数据初始化渲染层
     * @private
     */
    private build() {
        const logicService = new PlayerLogicService();
        this.slideComponent = new SlideComponent({
            slideModel: this.slideModel,
            getPageScale: this.getPageScale,
            getApplication: this.getApplication,
            region: RenderPostionType.PLAYER,
            fromScale: 1,
            logicService,
            handleSlideClick: this.handleSlideClick,
            asyncBuild: this.asyncBuild,
            getPlayerReduxStore: this.getPlayerReduxStore,
            onDidRenderCallback: this.onDidRenderCallback,
            infrastructureService: this.getAppServices().getInfrastructureService(),
            uiDataService: this.getAppServices().getUiDataService(),
            mediaManager: this.getAppServices().viewableServiceLayer.mediaManager,
            editingStateManager: this.getAppServices().viewableServiceLayer.editingStateManager,
            addonManager: this.getAppServices().viewableServiceLayer.addonManager,
            fontManager: this.getAppServices().viewableServiceLayer.fontManager,
            linkManager: this.getAppServices().viewableServiceLayer.linkManager,
            accessibilityManager: this.getAppServices().viewableServiceLayer.accessibilityManager,
            gifService: this.getAppServices().viewableServiceLayer.gifService,
        });
    }

    private getAppServices(): ISlideBaseAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            ISlideBaseAppServices;
    }
}
